import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection } from 'constants/index'
import { orderBy } from 'lodash'
import { signOut } from 'store/auth'
import { toast } from 'utils/notify'
import { cloudFunctionsInstance } from 'config/axios'

export const initialState = {
  error: null,
  inProgress: false,
  announcements: [],
  announcement: null,
  currentPage: 0,
  pageSize: 10
}

const announcementSlice = createSlice({
  name: 'announcement',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setAnnouncements: (state, action) => {
      const announcements = action.payload
      const scheduledAnnouncements = announcements.filter((ann) => ann.scheduledOn)
      const unScheduledAnnouncements = announcements.filter((ann) => !ann.scheduledOn)
      const scheduledSorted = orderBy(
        scheduledAnnouncements,
        [(obj) => new Date(obj.scheduledOn)],
        ['asc']
      )
      const unScheduledSorted = orderBy(
        unScheduledAnnouncements,
        [(obj) => new Date(obj.publishedOn)],
        ['desc']
      )
      state.announcements = [...scheduledSorted, ...unScheduledSorted]
    },
    setAnnouncement: (state, action) => {
      state.announcement = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.announcements = []
      state.announcement = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const fetchAnnouncements = () => async (dispatch, getState) => {
  try {
    const user = getState().auth.user
    dispatch(announcementSlice.actions.setProgress(true))
    await FirestoreDatabase.collection(Collection.ANNOUNCEMENTS)
      .orderBy('publishedOn', 'desc')
      .where('conferenceId', '==', user?.conferenceId)
      .onSnapshot((documents) => {
        const announcements = []
        documents.forEach((doc) => {
          announcements.push({
            id: doc.id,
            ...doc.data()
          })
        })
        dispatch(announcementSlice.actions.setAnnouncements(announcements))
      })
  } catch (err) {
    dispatch(announcementSlice.actions.setError(err))
  } finally {
    dispatch(announcementSlice.actions.setProgress(false))
  }
}

export const addAnnouncement = (payload) => async (dispatch, getState) => {
  try {
    const user = getState().auth.user
    dispatch(announcementSlice.actions.setProgress(true))
    const doc = await FirestoreDatabase.collection(Collection.ANNOUNCEMENTS).add({
      ...payload,
      conferenceId: user?.conferenceId,
      // Stamp the creating admin so the notification log can show who initiated
      // it. Set on create only — edits preserve the original author.
      createdBy: user?.id ?? null
    })
    return doc.id
  } catch (err) {
    dispatch(announcementSlice.actions.setError(err))
  } finally {
    dispatch(announcementSlice.actions.setProgress(false))
  }
}

export const fetchAnnouncementById = (announcementId) => async (dispatch) => {
  try {
    dispatch(announcementSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.ANNOUNCEMENTS)
      .doc(announcementId)
      .get()
    const e = snapshot.data()
    dispatch(
      announcementSlice.actions.setAnnouncement({
        id: snapshot.id,
        ...e
      })
    )
  } catch (err) {
    dispatch(announcementSlice.actions.setError(err))
  } finally {
    dispatch(announcementSlice.actions.setProgress(false))
  }
}

export const updateAnnouncementById = (announcementId, payload) => (dispatch) => {
  try {
    dispatch(announcementSlice.actions.setProgress(true))
    FirestoreDatabase.collection(Collection.ANNOUNCEMENTS)
      .doc(announcementId)
      .update({
        ...payload
      })
  } catch (err) {
    dispatch(announcementSlice.actions.setError(err))
  } finally {
    dispatch(announcementSlice.actions.setProgress(false))
  }
}

export const deleteAnnouncementById = (announcementId) => async (dispatch) => {
  try {
    dispatch(announcementSlice.actions.setProgress(true))
    await FirestoreDatabase.collection(Collection.ANNOUNCEMENTS).doc(announcementId).delete()
    toast.success('Announcement deleted.')
  } catch (err) {
    dispatch(announcementSlice.actions.setError(err))
  } finally {
    dispatch(announcementSlice.actions.setProgress(false))
  }
}

export const sendAnnouncement = (announcementId) => async (dispatch, getState) => {
  try {
    const user = getState().auth.user
    await cloudFunctionsInstance.post('/sendAnnouncement', {
      conferenceId: user?.conferenceId,
      announcementId,
      isScheduled: false
    })
  } catch (err) {
    dispatch(announcementSlice.actions.setError(err))
  }
}

export const resetAnnouncement = () => (dispatch) => {
  dispatch(announcementSlice.actions.setAnnouncement(null))
}

export default announcementSlice
